import React from 'react';
import { FiChevronUp, FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export interface Column {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (row: any) => React.ReactNode;
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  sortBy?: string;
  sortDescending?: boolean;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (key: string) => void;
  onRowClick?: (row: any) => void;
  // Selection support
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  idKey?: string;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  totalPages,
  sortBy,
  sortDescending,
  onPageChange,
  onPageSizeChange,
  onSort,
  onRowClick,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  idKey = 'id',
}) => {
  const allPageIds = data.map((row) => row[idKey] as string);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));
  const someSelected = allPageIds.some((id) => selectedIds.has(id));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (allSelected) {
      allPageIds.forEach((id) => next.delete(id));
    } else {
      allPageIds.forEach((id) => next.add(id));
    }
    onSelectionChange(next);
  };

  const toggleOne = (id: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div className="pagination">
        <span className="pagination-info">
          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} z {totalCount}
          {selectable && selectedIds.size > 0 && (
            <span style={{ marginLeft: '12px', fontWeight: 600, color: 'var(--primary)' }}>
              (zaznaczono: {selectedIds.size})
            </span>
          )}
        </span>
        <div className="pagination-buttons">
          <button
            className="pagination-btn"
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
          >
            <FiChevronLeft size={14} />
          </button>
          {pages.map((p, i) =>
            typeof p === 'number' ? (
              <button
                key={i}
                className={`pagination-btn ${p === page ? 'active' : ''}`}
                onClick={() => onPageChange?.(p)}
              >
                {p}
              </button>
            ) : (
              <span key={i} className="pagination-btn" style={{ border: 'none', cursor: 'default', opacity: 0.5 }}>
                ...
              </span>
            )
          )}
          <button
            className="pagination-btn"
            disabled={page >= totalPages}
            onClick={() => onPageChange?.(page + 1)}
          >
            <FiChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="data-table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => col.sortable && onSort?.(col.key)}
                className={sortBy === col.key ? 'sorted' : ''}
                style={{ cursor: col.sortable ? 'pointer' : 'default' }}
              >
                {col.header}
                {col.sortable && sortBy === col.key && (
                  <span className="sort-icon">
                    {sortDescending ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)}>
                <div className="empty-state">
                  <p>Brak danych do wyswietlenia</p>
                </div>
              </td>
            </tr>
          ) : (
            data.map((row, i) => {
              const rowId = row[idKey] as string;
              const isSelected = selectedIds.has(rowId);
              return (
                <tr
                  key={rowId || i}
                  onClick={() => onRowClick?.(row)}
                  className={`${onRowClick ? 'clickable' : ''} ${isSelected ? 'selected-row' : ''}`}
                  style={isSelected ? { background: 'var(--primary-light, #eff6ff)' } : {}}
                >
                  {selectable && (
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(rowId)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {renderPagination()}
    </div>
  );
};

export default DataTable;
